import clsx from 'clsx'
import DropdownSortingMenu from 'components/DropdownSortingMenu'
import GridSpinner from 'components/UI/GridSpinner'
import { Button, Col, Grid, Icon, List, Spinner } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import EmptyResults from 'components/EmptyResults'
import { ReactComponent as EmptyResultsSVG } from 'images/illustration/no-advertisers.svg'
import { useTableConfig } from 'modules/Services/Network/subcomponents/AccountsList/hooks/useTableConfig'
import FilterPanel from 'components/Filters/FilterPanel'
import { useGroupActionConfig } from 'modules/Services/Network/subcomponents/AccountsList/hooks/useGroupActionConfig'
import styles from 'modules/Services/Network/subcomponents/AccountsList/account-list.module.scss'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'
import { Link } from 'react-router-dom'
import { notificationShow } from 'modules/Notifications/actions'
import { createPath } from 'history'
import qs from 'qs'
import {
  ACCOUNTS_ARCHIVE_TOGGLE,
  NETWORK_SET_SORTING,
  SET_SELECTION,
  UPDATE_ACCOUNT_NAME,
} from 'modules/Services/types'
import { useGetConnectionLink } from 'utils/hooks/useGetConnectionLink'

const AccountList = ({
  display_balance = true,
  networkType,
  filterManager,
  networkId,
  setPage,
}) => {
  const { t } = useTranslation(['services'])
  const connectionLink = useGetConnectionLink(networkType)
  const pathGen = useGeneratePathI18n()
  const dispatch = useDispatch()
  const [getActionConfig, runGroupAction] = useGroupActionConfig()

  const {
    i18n,
    network: {
      accounts,
      has_account,
      selection: { ids: selectionIds = [], processing: selectionProcessing },
      texts = [],
    },
  } = useSelector((state) => ({
    i18n: state['app'].i18n,
    network: state['services/network'],
  }))

  const { pagination, data, sorting, loading, loaded } = accounts

  const canBeDeleted = useMemo(
    () => ['vk', 'vk-ads', 'targetmail'].includes(networkType),
    [networkType],
  )

  const setSorting = useCallback(
    (value) => dispatch({ type: NETWORK_SET_SORTING, payload: value }),
    [],
  )

  const processGroupAction = useCallback(
    (groupActionType) => {
      const groupActionConfig = getActionConfig(groupActionType)
      runGroupAction({ groupActionConfig, ids: selectionIds })
    },
    [selectionIds, getActionConfig],
  )

  const updateSelection = (list) => {
    dispatch({
      type: SET_SELECTION,
      payload: list,
    })
  }

  const applyAccountName = useCallback(
    (id, newValue) =>
      new Promise((resolve, reject) =>
        dispatch({
          type: UPDATE_ACCOUNT_NAME,
          payload: { id, name: newValue },
          meta: { resolve, reject },
        }),
      ).then(() =>
        dispatch(
          notificationShow({
            type: 'success',
            message: 'common::notifications.data_success_stored',
            delay: 2000,
          }),
        ),
      ),
    [],
  )

  const { tableState, tableConfig } = useTableConfig(
    {
      display_balance,
      updateSelection,
      selectionIds,
      i18n,
      networkType,
      filters: filterManager.filters,
      applyAccountName,
    },
    data,
  )

  const manualHandleClick = useCallback(
    () =>
      dispatch({
        type: 'MODAL/SHOW',
        payload: {
          modalType: 'networkText',
          modalProps: {
            networkId,
            textKey: 'manual',
            shouldCloseOnOverlayClick: true,
          },
        },
      }),
    [networkId],
  )

  const canSelectedAccountsToppedUp = useMemo(
    () =>
      accounts?.data?.reduce((acc, { id, main_action }) => {
        if (selectionIds.includes(id) && main_action === 'deposit') {
          acc.push(id)
        }
        return acc
      }, []).length > 0,
    [selectionIds, accounts],
  )

  const toggleArchiveGroupAction = useCallback(
    (toArchive) => {
      dispatch({
        type: ACCOUNTS_ARCHIVE_TOGGLE,
        payload: {
          toArchive,
          ids: selectionIds,
        },
      })
    },
    [selectionIds],
  )

  return (
    <>
      <FilterPanel
        filterManager={filterManager}
        className={styles['accounts-list__filter-panel']}
      />
      {!loading && loaded && data.length !== 0 && (
        <DropdownSortingMenu
          className={styles['accounts-list__sort-menu']}
          buttonLabelPrefix={''}
          sortingRuleMap={
            display_balance
              ? {
                  created_at: {
                    asc: 'created_at.asc',
                    desc: 'created_at.desc',
                  },
                  balance: {
                    asc: 'balance.asc',
                    desc: 'balance.desc',
                  },
                }
              : {
                  created_at: {
                    asc: 'created_at.asc',
                    desc: 'created_at.desc',
                  },
                }
          }
          i18nKey={'services::account_list.filter_controls.sortmenu'}
          id={'accounts-list-sort-menu'}
          sorting={sorting}
          onSortChange={setSorting}
        />
      )}

      <div
        className={clsx(styles['accounts-list__group-actions-slider'], {
          [styles['accounts-list__group-actions-slider_active']]: !!selectionIds.length,
        })}
      >
        {!loading && (
          <Grid
            alignItems="center"
            justifyContent="between"
            className={styles['accounts-list__group-actions-wrapper']}
          >
            <Grid fluid={false} gap={16} className={styles['accounts-list__group-actions']}>
              {!filterManager?.filters?.is_archived && canSelectedAccountsToppedUp && (
                <Button
                  startIcon={<Icon inheritColor name="card" size="s" />}
                  variant={'primary'}
                  size={'s'}
                  component={Link}
                  to={createPath({
                    pathname: pathGen(ROUTES.BILLING_TOP_UP_ACCOUNTS, {
                      networkType,
                    }),
                    search: qs.stringify({ ids: selectionIds || [] }, { arrayFormat: 'brackets' }),
                  })}
                >
                  {t('services::account_list.actions.deposit')}
                </Button>
              )}

              {filterManager?.filters?.is_archived ? (
                <Button
                  type="button"
                  variant={'secondary'}
                  grey
                  size="s"
                  disabled={selectionProcessing}
                  startIcon={
                    selectionProcessing ? (
                      <Spinner inheritColor width={20} />
                    ) : (
                      <Icon inheritColor name="inbox-upload" size="s" />
                    )
                  }
                  onClick={() => toggleArchiveGroupAction(false)}
                >
                  {t('services::account_list.extra_actions.unarchive')}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant={'secondary'}
                  grey
                  size="s"
                  disabled={selectionProcessing}
                  startIcon={
                    selectionProcessing ? (
                      <Spinner inheritColor width={20} />
                    ) : (
                      <Icon inheritColor name="inbox-archive" size="s" />
                    )
                  }
                  onClick={() => toggleArchiveGroupAction(true)}
                >
                  {t('services::account_list.extra_actions.archive')}
                </Button>
              )}

              {canBeDeleted && (
                <Button
                  color={'basic-primary-black'}
                  className={styles['accounts-list__group-actions-button']}
                  type="button"
                  disabled={selectionProcessing}
                  size="s"
                  startIcon={
                    selectionProcessing ? (
                      <Spinner inheritColor width={20} />
                    ) : (
                      <Icon inheritColor name="delete" size="s" />
                    )
                  }
                  onClick={() => processGroupAction('group_delete')}
                >
                  {t('services::account_list.extra_actions.delete')}
                </Button>
              )}
            </Grid>
            <Col className={styles['accounts-list__group-actions-selected-accounts']}>
              {t('services::account_list.checkedAccounts', {
                count: selectionIds.length,
              })}
            </Col>
          </Grid>
        )}
      </div>

      {data.length === 0 && (!networkType || loading) && (
        <div className={styles['accounts-list__spinner-container']}>
          <GridSpinner width={32} style={{ height: '100%' }} />
        </div>
      )}

      {loaded && !loading && data.length === 0 ? (
        <EmptyResults
          header={t('services::account_list.data_list.empty.heading')}
          text={
            has_account ? (
              t('services::account_list.data_list.empty.text')
            ) : texts.find(({ key }) => key === 'manual') ? (
              <Trans
                i18nKey="services::account_list.rules_for_connecting"
                link={t('services::account_list.rules_for_connecting_link')}
              >
                Ознакомьтесь c
                <Button
                  color={'Label-GU-Primary'}
                  size={'l'}
                  variant={'link'}
                  type={'button'}
                  onClick={manualHandleClick}
                >
                  правилами подключения
                </Button>
                аккаунтов
              </Trans>
            ) : null
          }
          image={<EmptyResultsSVG />}
          actionButton={
            <Button
              startIcon={<Icon size={'m'} name={'plus'} />}
              component={Link}
              to={pathGen(connectionLink, { networkType })}
              variant={'contained'}
              size={'m'}
            >
              {t('services::account_list.add_new_account', {
                context: networkType,
              })}
            </Button>
          }
        />
      ) : loaded && data.length !== 0 ? (
        <>
          <List
            Loader={() => <GridSpinner width={32} style={{ height: '100%' }} />}
            table={tableState}
            config={tableConfig}
            EmptyComponent={
              !loading && data.length === 0
                ? () => (
                    <EmptyResults
                      header={t('services::account_list.empty_list_heading')}
                      text={
                        has_account ? (
                          t('services::account_list.empty_list_label')
                        ) : texts.find(({ key }) => key === 'manual') ? (
                          <Trans
                            i18nKey="services::account_list.rules_for_connecting"
                            link={t('services::account_list.rules_for_connecting_link')}
                          >
                            Ознакомьтесь c
                            <Button
                              color={'Label-GU-Primary'}
                              size={'l'}
                              variant={'link'}
                              type={'button'}
                              onClick={manualHandleClick}
                            >
                              правилами подключения
                            </Button>
                            аккаунтов
                          </Trans>
                        ) : null
                      }
                      className={styles['accounts-list__empty-results']}
                      image={<EmptyResultsSVG />}
                    />
                  )
                : null
            }
          />
          {pagination.total !== data.length && (
            <div className={styles['pagination']}>
              <Grid
                alignItems={'center'}
                justifyContent={'center'}
                className={styles['show-more__container']}
              >
                {pagination.total !== data.length && (
                  <Button
                    onClick={() => setPage(pagination.page + 1)}
                    className={clsx(styles['show-more__button'])}
                    variant={'link'}
                    startIcon={loading ? <Spinner width={16} color={'inherit'} /> : null}
                    endIcon={<Icon name={'arrow1-down'} color={'inherit'} size={'s'} />}
                  >
                    {t('common::pagination.show_more')}
                  </Button>
                )}
              </Grid>
            </div>
          )}
        </>
      ) : null}
    </>
  )
}

AccountList.propTypes = {
  networkType: PropTypes.string,
  filterManager: PropTypes.object,
  networkId: PropTypes.string,
  display_balance: PropTypes.bool,
  accounts: PropTypes.object,
  setPage: PropTypes.func,
}

export default AccountList
